/**
 * Style tokens and theming for Deckspace UI.
 *
 * All token unions and the ComponentStyle / SurfaceTheme shapes that consume them.
 * Pure types — no runtime values.
 */

/**
 * Color scheme (dark/light mode)
 */
export type ColorScheme = 'dark' | 'light';

/**
 * Connection state for status indicators.
 */
export type ConnectionState = 'connected' | 'connecting' | 'waiting' | 'error' | 'disconnected';

/**
 * Connection status info displayed in settings.
 */
export interface ConnectionStatus {
  state: ConnectionState;
  /** Port number if connected */
  port?: number | null;
  /** Error message if connection failed */
  error?: string | null;
}

/**
 * User preferences stored server-side (JSONB bag)
 * All fields optional — missing means "use default"
 */
export interface UserPreferences {
  colorScheme?: ColorScheme;
}

export const DEFAULT_USER_PREFERENCES: Required<UserPreferences> = {
  colorScheme: 'dark',
};

/**
 * Theme palette (color family)
 * Only 'deckspace' palette is supported
 */
export type ThemePalette = 'deckspace';

/**
 * Theme name
 * - deckspace-dark: Premium dark mode (default)
 * - deckspace-light: Clean, professional light mode
 */
export type ThemeName = 'deckspace-dark' | 'deckspace-light';

/**
 * Color intent for semantic meaning in visualizations
 * - neutral: Default distinct colors, no semantic meaning
 * - positive-negative: Green vs red for profit/loss, budget/deficit
 * - comparison: Blue vs orange for A/B without value judgment
 * - diverging: Red-gray-blue for correlation or deviation
 */
export type ColorIntent = 'neutral' | 'positive-negative' | 'comparison' | 'diverging';

// --- Style Tokens (Catalog v2) ---

export type BgToken = 'surface' | 'surface-raised' | 'primary' | 'accent' | 'muted' | 'transparent';
export type ColorToken =
  | 'foreground'
  | 'muted'
  | 'primary'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger';
export type SpacingToken = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type RadiusToken = 'none' | 'sm' | 'md' | 'lg' | 'full';
export type BorderToken = 'none' | 'default' | 'muted' | 'accent';
export type ShadowToken = 'none' | 'sm' | 'md' | 'lg';
export type SizeValue = number | 'full' | 'auto' | 'half';

export interface ComponentStyle {
  bg?: BgToken;
  color?: ColorToken;
  padding?: SpacingToken;
  margin?: SpacingToken;
  gap?: SpacingToken | number;
  radius?: RadiusToken;
  border?: BorderToken;
  shadow?: ShadowToken;
  width?: SizeValue;
  height?: number | 'full' | 'auto';
  minHeight?: number;
  opacity?: number;
}

export type PaletteToken =
  | 'blue'
  | 'emerald'
  | 'amber'
  | 'rose'
  | 'violet'
  | 'cyan'
  | 'orange'
  | 'slate';

export type PalettePreset =
  | 'default'
  | 'warm'
  | 'cool'
  | 'earth'
  | 'monochrome'
  | 'ocean'
  | 'sunset';

export interface SurfaceTheme {
  palette?: PalettePreset;
  primary?: PaletteToken;
  accent?: PaletteToken;
  colorIntent?: ColorIntent;
}
